import { Box, Button, FormControlLabel, Typography, Checkbox } from '@mui/material';
import { brand } from 'src/shared/branding/brand';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { AddCircleOutline, Delete } from '@mui/icons-material';

import {
  Header,
  HeadingSummaryBlock,
  FormWrapper,
  ContentContainerBottom,
  FormInput,
  FormInputPhoneNumber,
  Messages,
  FormSelectionVerifyBusinessStructure,
  ManualLoader,
} from 'src/components/base/core';

import { dtoToBusinessOwnershipRepFormValues } from 'src/shared/utilities/dto';
import { useGetBlockHeight } from 'src/hooks/useGetBlockHeight';
import { theme, commonSpacing } from 'src/shared/theme/theme';
import { AccountParams } from 'src/shared/utilities/interface';
import { getCookie } from 'src/shared/utilities/cookiesHelper';

import { handleBusinessRepOwnership } from 'src/helpers/containers/helperBusinessOwnership';
import { businessVerifyOwnershipSchemaObj } from 'src/helpers/validationSchemas';
import { IRelationShip } from 'src/models/api/account';
import { initialRelationShipObject } from 'src/models/init/account.init';
import { decryptWithAES } from 'src/shared/utilities/encryptHelpper';
import { configs } from 'src/shared/config/config';

interface OwnerDetails {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  is_representative: boolean;
  is_director: boolean;
  is_executive: boolean;
  is_owner: boolean;
  title: string;
}

interface FormValues extends AccountParams {
  owners: OwnerDetails[];
}

export const FromVerifyRepresentSubmition = ({
  onBackButtonClick,
  onFormSubmit,
  accountData,
  onSkipSecondStep,
  isFromFinance,
}: any) => {
  // handle the featured flags __
  const { webauthnEnabled } = useFlags();
  // set the translation __
  const { t } = useTranslation();
  // call the handleBusinessRepOwnership(); helper function and it's return values __
  const [appSubmit, isLoading, onChangeHandlerPhone, serverSideErrors, setServerSideErrors]: any =
    handleBusinessRepOwnership(accountData);
  // call the useGetBlockHeight() custom hook to handle the container height in desktop view __
  const [elementRef, height] = useGetBlockHeight();

  // initialize the states __
  const [radioChecked, setRadioChecked] = useState<string>(''); // used to set the selected radio box id  (owner | executive), based on this radio button selection will handled
  const [btnSubmitted, setBtnSubmitted] = useState<boolean>(false); // used to make sure the button got submitted
  const [errorOwnership, setErrorOwnership] = useState<string>(''); // used to handle the error message of radio button selection
  const [relationshipObj, setRelationshipObj] = useState<IRelationShip>(initialRelationShipObject);
  const [perCentageOwnershipConfirmed, setPerCentageOwnershipConfirmed] = useState(false);
  const [owners, setOwners] = useState<OwnerDetails[]>([
    {
      first_name: '',
      last_name: '',
      email: '',
      phone_number: '',
      is_representative: true, // First owner is default representative
      is_director: false,
      is_executive: false,
      is_owner: false,
      title: 'Manager',
    },
  ]);

  // handle re-renders and inital load the values in to form elements based on the accountData load__
  useEffect(() => {
    if (accountData) {
      const isSoleOwnerCompanyStructure =
        accountData?.company_structure === 'sole_proprietorship' ? true : false;

      if (isSoleOwnerCompanyStructure) {
        setRadioChecked('yes');
        setRelationshipObj({
          director: true, // This person is a member of the governing board of the company: relationship[director]
          executive: true, // This person is an executive or senior manager with significant management responsibility: relationship[executive]
          owner: true, // This person owns 25% or more of the company: relationship[owner]
          percent_ownership: 100,
          representative: true, // This person is authorized as the primary representative of the account: relationship[representative]
        });
        setValue('company_structure', 'sole_proprietorship');
        setPerCentageOwnershipConfirmed(false);
      } else {
        setRadioChecked('no');
        setValue('company_structure', null);

        const getRelaObj = accountData?.individual?.relationShip;

        const isPercentageConfirmButtonClicked =
          !getRelaObj?.director &&
          getRelaObj?.executive &&
          getRelaObj?.owner &&
          getRelaObj?.representative &&
          getRelaObj?.percent_ownership === 25;

        if (isPercentageConfirmButtonClicked) {
          setPerCentageOwnershipConfirmed(true);
          setRelationshipObj({
            director: false, // This person is a member of the governing board of the company: relationship[director]
            executive: true, // This person is an executive or senior manager with significant management responsibility: relationship[executive]
            owner: true, // This person owns 25% or more of the company: relationship[owner]
            percent_ownership: 25,
            representative: true, // This person is authorized as the primary representative of the account: relationship[representative]
          });
        } else {
          setPerCentageOwnershipConfirmed(false);
          setRelationshipObj({
            director: false, // This person is a member of the governing board of the company: relationship[director]
            executive: true, // This person is an executive or senior manager with significant management responsibility: relationship[executive]
            owner: false, // This person owns 25% or more of the company: relationship[owner]
            percent_ownership: null,
            representative: true, // This person is authorized as the primary representative of the account: relationship[representative]
          });
        }
      }

      // set the rest the values into relavant field
      dtoToBusinessOwnershipRepFormValues(getValues, setValue, accountData);
      setRepresentativeOwner(accountData.individual, accountData.other_owners);

      // set the full name, email, phone from cookies
      if (!accountData.individual.email && getCookie('coupaObj')) {
        if (webauthnEnabled) {
          const prefilData = decryptWithAES(
            getCookie('coupaObj'),
            configs.ENCRYPT_PHRESE
          ) as string;
          const data = prefilData && JSON.parse(prefilData);
          setRepresentativeOwner(data, []);
        }
      }
    }
  }, [accountData]);

  // handle the form elements validation using yup __
  const revieInfoSchema = yup
    .object({
      owners: yup
        .array()
        .of(
          yup.object(businessVerifyOwnershipSchemaObj).required('At least one owner is required')
        ),
    })
    .required();

  // initialize the hook-forms __
  const {
    register,
    handleSubmit,
    setValue,
    control,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(revieInfoSchema),
    defaultValues: {
      owners: [
        {
          first_name: '',
          last_name: '',
          email: '',
          phone_number: '',
          is_representative: true,
          is_director: false,
          is_executive: false,
          is_owner: false,
          title: '',
        },
      ],
    },
  });

  // handle the form submit __
  const onAppFormSubmit = handleSubmit(async (data: any) => {
    const updatedData = {
      ...relationshipObj,
      ...data,
    };

    if (isFromFinance) {
      if (radioChecked !== '' && perCentageOwnershipConfirmed) {
        appSubmit(updatedData, onFormSubmit);
        setErrorOwnership('');
      } else {
        if (radioChecked === '' && perCentageOwnershipConfirmed) {
          setErrorOwnership('Please provide more information regarding ownership of the company.');
        } else if (radioChecked === 'no' && !perCentageOwnershipConfirmed) {
          setErrorOwnership(
            'Please ensure the representative has a 25% or more ownership in the company.'
          );
        } else if (radioChecked === 'yes' && !perCentageOwnershipConfirmed) {
          appSubmit(updatedData, onFormSubmit);
          setErrorOwnership('');
        }
      }
    } else {
      if (radioChecked !== '') {
        appSubmit(
          updatedData,
          radioChecked === 'no' && !isFromFinance ? onSkipSecondStep : onFormSubmit
        );
        setErrorOwnership('');
      } else {
        setErrorOwnership('Please provide more information regarding ownership of the company.');
      }
    }
  });

  // update the values via hookform setValue method __
  const updateBindingsChange = (event: any, feild: any) => {
    setValue(feild, event.target.value, { shouldValidate: btnSubmitted && true });
  };

  // handle the phone number onchange event __
  const onChangeHandler = (event: any, index: number) => {
    onChangeHandlerPhone(event, setValue);
  };

  // handle the radio buttons related to business tructure and relationship __
  const handleRep = (val: any) => {
    const { id } = val.target; // get the id from selected element
    setRadioChecked(id); // set the setRadioChecked state

    if (id === 'yes') {
      setRelationshipObj({
        director: true, // This person is a member of the governing board of the company: relationship[director]
        executive: true, // This person is an executive or senior manager with significant management responsibility: relationship[executive]
        owner: true, // This person owns 25% or more of the company: relationship[owner]
        percent_ownership: 100,
        representative: true, // This person is authorized as the primary representative of the account: relationship[representative]
      });
      setValue('company_structure', 'sole_proprietorship'); // if selected target id is 'owner' set the company_structure value in to form-data output
      setPerCentageOwnershipConfirmed(false);
    } else if (id === 'no') {
      setRelationshipObj({
        director: false, // This person is a member of the governing board of the company: relationship[director]
        executive: true, // This person is an executive or senior manager with significant management responsibility: relationship[executive]
        owner: false, // This person owns 25% or more of the company: relationship[owner]
        percent_ownership: null,
        representative: true, // This person is authorized as the primary representative of the account: relationship[representative]
      });
      setValue('company_structure', null); // if selected target id is 'executive' set the company_structure value in to form-data output
    }
  };

  const onChnageOwnerPercentage = (val: any) => {
    if (val.target.checked) {
      setPerCentageOwnershipConfirmed(true);
      setRelationshipObj({
        director: false, // This person is a member of the governing board of the company: relationship[director]
        executive: true, // This person is an executive or senior manager with significant management responsibility: relationship[executive]
        owner: true, // This person owns 25% or more of the company: relationship[owner]
        percent_ownership: 25,
        representative: true, // This person is authorized as the primary representative of the account: relationship[representative]
      });
    } else {
      setPerCentageOwnershipConfirmed(false);

      if (radioChecked === 'yes') {
        setRelationshipObj({
          director: true, // This person is a member of the governing board of the company: relationship[director]
          executive: true, // This person is an executive or senior manager with significant management responsibility: relationship[executive]
          owner: true, // This person owns 25% or more of the company: relationship[owner]
          percent_ownership: 100,
          representative: true, // This person is authorized as the primary representative of the account: relationship[representative]
        });
        setValue('company_structure', 'sole_proprietorship'); // if selected target id is 'owner' set the company_structure value in to form-data output
      } else if (radioChecked === 'no') {
        setRelationshipObj({
          director: false, // This person is a member of the governing board of the company: relationship[director]
          executive: true, // This person is an executive or senior manager with significant management responsibility: relationship[executive]
          owner: false, // This person owns 25% or more of the company: relationship[owner]
          percent_ownership: null,
          representative: true, // This person is authorized as the primary representative of the account: relationship[representative]
        });
        setValue('company_structure', null); // if selected target id is 'executive' set the company_structure value in to form-data output
      }
    }
  };

  const handleRepresentativeChange = (selectedIndex: number) => {
    const newOwners = owners.map((owner, index) => ({
      ...owner,
      is_representative: index === selectedIndex,
    }));
    setOwners(newOwners);
    setValue('owners', newOwners);
  };

  const addOwner = () => {
    if (owners.length < 4) {
      const newOwner: OwnerDetails = {
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        is_representative: false, // Additional owners are never representatives
        is_director: false,
        is_executive: false,
        is_owner: false,
        title: '',
      };
      const newOwners = [...owners, newOwner];
      setOwners(newOwners);
      setValue('owners', newOwners);
    }
  };

  const removeOwner = (index: number) => {
    const newOwners = owners.filter((_, i) => i !== index);
    setOwners(newOwners);
    setValue('owners', newOwners);
  };

  const setRepresentativeOwner = (data: any, otherOwners: any[] = []) => {
    const owners = [data, ...otherOwners].map((owner: any, index) => ({
      first_name: owner.name ?? owner.first_name,
      last_name: owner.lastName ?? owner.last_name,
      email: owner.email,
      phone_number: owner.phone?.replace('+1', ''),
      is_representative: index === 0, // First owner is always representative
      is_director: owner.relationShip?.director,
      is_executive: owner.relationShip?.executive,
      is_owner: owner.relationShip?.owner,
      title: owner.relationShip?.title ?? 'Manager',
    }));

    setOwners(owners);
    setValue('owners', owners);
  };

  return (
    <>
      {isLoading && <ManualLoader />}
      <FormWrapper onSubmit={onAppFormSubmit} className="no_fullscreen">
        <Box component={'div'} className={'container_content'}>
          <Box
            component={'div'}
            className={'container_content_top'}
            ref={elementRef}
            sx={{
              [`@media(min-width: ${theme.breakpoints.values.sm}px)`]: {
                minHeight: height,
                paddingBottom: height ? 4 : commonSpacing.desktopPaddingB,
              },
            }}
          >
            <Header
              isBackButtonVisible={true}
              onBackButtonClick={onBackButtonClick}
              isProgressBarVisible={true}
              totalProgressSteps={6}
              currentProgressStep={2}
              paddingX={0}
            />
            <Box sx={{ marginBottom: 6 }}>
              <HeadingSummaryBlock
                heading={'Benefical Owners(Upto 4)'}
                headingAlignment="left"
                content={t('headingContent.business.ownerInfo.content')}
                contentAlignment="left"
              />
            </Box>

            <FormSelectionVerifyBusinessStructure
              radioChecked={radioChecked}
              handleRep={handleRep}
              t={t}
              ownershipPercentageSelectionEnabled={isFromFinance && radioChecked === 'no'}
              chnageOwnerPercentage={onChnageOwnerPercentage}
              ownerPercentageChecked={perCentageOwnershipConfirmed}
            />
            {owners.map((owner, index) => (
              <Box
                key={index}
                sx={{
                  position: 'relative',
                  mb: 4,
                  p: 2,
                  border: '1px solid #eee',
                  borderRadius: 2,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                    borderBottom: '1px solid #eee',
                    pb: 2,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontSize: '1rem',
                      fontWeight: 500,
                    }}
                  >
                    Owner #{index + 1}
                  </Typography>
                  {owners.length > 1 && index !== 0 && (
                    <Button
                      onClick={() => removeOwner(index)}
                      color="error"
                      size="small"
                      startIcon={<Delete />}
                    >
                      Remove
                    </Button>
                  )}
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  {index === 0 ? (
                    <Typography
                      sx={{
                        backgroundColor: 'primary.main',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: 1,
                        fontSize: '0.875rem',
                      }}
                    >
                      Account Representative
                    </Typography>
                  ) : (
                    <Typography
                      sx={{
                        color: 'text.secondary',
                        fontSize: '0.875rem',
                      }}
                    >
                      Additional Owner
                    </Typography>
                  )}
                </Box>

                <Controller
                  control={control}
                  name={`owners.${index}.title`}
                  render={({ field }) => (
                    <FormInput
                      {...field}
                      label="Title"
                      id={`title_${index}`}
                      placeholder="Enter title"
                      error={errors?.owners?.[index]?.title?.message}
                      onChange={(event: any) => {
                        field.onChange(event.target.value);
                        const newOwners = [...owners];
                        newOwners[index] = { ...newOwners[index], title: event.target.value };
                        setOwners(newOwners);
                      }}
                    />
                  )}
                />

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 2,
                    mb: 2,
                    flexWrap: 'wrap',
                  }}
                >
                  <Controller
                    control={control}
                    name={`owners.${index}.is_director`}
                    render={({ field: { onChange, value } }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={value}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              onChange(checked);
                              const newOwners = [...owners];
                              newOwners[index] = {
                                ...newOwners[index],
                                is_director: checked,
                              };
                              setOwners(newOwners);
                            }}
                            color="primary"
                          />
                        }
                        label="Director"
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name={`owners.${index}.is_executive`}
                    render={({ field: { onChange, value } }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={value}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              onChange(checked);
                              const newOwners = [...owners];
                              newOwners[index] = {
                                ...newOwners[index],
                                is_executive: checked,
                              };
                              setOwners(newOwners);
                            }}
                            color="primary"
                          />
                        }
                        label="Executive"
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name={`owners.${index}.is_owner`}
                    render={({ field: { onChange, value } }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={value}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              onChange(checked);
                              const newOwners = [...owners];
                              newOwners[index] = {
                                ...newOwners[index],
                                is_owner: checked,
                              };
                              setOwners(newOwners);
                            }}
                            color="primary"
                          />
                        }
                        label="Owner(>25%)"
                      />
                    )}
                  />
                </Box>

                <Controller
                  control={control}
                  name={`owners.${index}.first_name`}
                  render={({ field }) => (
                    <FormInput
                      {...field}
                      label={t('formInput.firstName')}
                      id={`first_name_${index}`}
                      placeholder={t('placeHolder.firstName')}
                      error={errors?.owners?.[index]?.first_name?.message}
                      onChange={(event: any) => {
                        event.target.value = event.target.value.replace('  ', ' ');
                        field.onChange(event.target.value);
                        // Update owners state
                        const newOwners = [...owners];
                        newOwners[index] = { ...newOwners[index], first_name: event.target.value };
                        setOwners(newOwners);
                      }}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name={`owners.${index}.last_name`}
                  render={({ field }) => (
                    <FormInput
                      {...field}
                      label={t('formInput.lastName')}
                      id={`last_name_${index}`}
                      placeholder={t('placeHolder.lastName')}
                      error={errors?.owners?.[index]?.last_name?.message}
                      onChange={(event: any) => {
                        event.target.value = event.target.value.replace('  ', ' ');
                        field.onChange(event.target.value);
                        // Update owners state
                        const newOwners = [...owners];
                        newOwners[index] = { ...newOwners[index], last_name: event.target.value };
                        setOwners(newOwners);
                      }}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name={`owners.${index}.email`}
                  render={({ field }) => (
                    <FormInput
                      {...field}
                      label={t('formInput.email')}
                      id={`email_${index}`}
                      placeholder={t('placeHolder.emailAddress')}
                      inputMode="email"
                      error={errors?.owners?.[index]?.email?.message}
                      onChange={(event: any) => {
                        field.onChange(event.target.value);
                        // Update owners state
                        const newOwners = [...owners];
                        newOwners[index] = { ...newOwners[index], email: event.target.value };
                        setOwners(newOwners);
                      }}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name={`owners.${index}.phone_number`}
                  render={({ field }) => (
                    <FormInputPhoneNumber
                      {...field}
                      label={t('formInput.phone')}
                      id={`phone_${index}`}
                      placeholder={'(123) 543-3454'}
                      inputError={errors?.owners?.[index]?.phone_number?.message}
                      onChange={(maskedValue: string) => {
                        field.onChange(maskedValue); // Update form field
                        const newOwners = [...owners];
                        newOwners[index] = {
                          ...newOwners[index],
                          phone_number: maskedValue,
                        };
                        setOwners(newOwners);
                      }}
                      inputMode="numeric"
                    />
                  )}
                />
              </Box>
            ))}

            {radioChecked !== 'yes' && owners.length < 4 && (
              <Button
                fullWidth
                variant="outlined"
                onClick={addOwner}
                startIcon={<AddCircleOutline />}
                sx={{ mt: 2 }}
              >
                Add Another Owner
              </Button>
            )}

            {serverSideErrors && (
              <Messages
                topMargin={0}
                bottomMargin={2}
                messageHeading={
                  serverSideErrors && serverSideErrors.includes('phone')
                    ? t('errorMessages.phoneUniqueError.heading')
                    : serverSideErrors.includes('email')
                    ? t('errorMessages.emailUniqueError.heading')
                    : serverSideErrors
                }
                messageContent={
                  serverSideErrors.includes('phone')
                    ? t('errorMessages.phoneUniqueError.content')
                    : serverSideErrors.includes('email')
                    ? t('errorMessages.emailUniqueError.content')
                    : `Please provide the valid details to use ${brand.displayName}.`
                }
                closeEvent={() => setServerSideErrors('')}
              />
            )}

            {errorOwnership && errorOwnership !== '' && (
              <Messages
                topMargin={0}
                bottomMargin={0}
                messageHeading={'A required field is missing'}
                messageContent={errorOwnership}
                closeEvent={() => setErrorOwnership('')}
              />
            )}
          </Box>
        </Box>
        <ContentContainerBottom className={'no_fullscreen'}>
          <Button
            variant="containedLarge"
            color="primary"
            type="submit"
            fullWidth
            sx={{ overflow: 'hidden' }}
            onClick={() => {
              setBtnSubmitted(true);
            }}
            // disabled={buttonState}
          >
            {t('buttonTexts.continueButton')}
          </Button>
        </ContentContainerBottom>
      </FormWrapper>
    </>
  );
};
